/**
 * POPULATIONS-FILTER-PANEL.TSX
 * Filter populations by risks
 */

import ListItem from "@/components/list-item"
import FilterSidePanel from "@/components/FilterSidePanel"
import { RiskFamilyFilterBlock, SiteFilterBlock } from "@/components/FilterBlock"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import PopulationsSearch from "./PopulationSearch"
import RiskSort from "@/entities/physical-risk/ui/RiskSort"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import { flatten } from "lodash"
import siteStore from "@/entities/site/model/site.store"
import { RISK_FAMILY_UNDEFINED_ID } from "@/entities/risk-family/model/risk-family.types"
import populationStore from "../model/population.store"

export default function PopulationsFilterPanel() {
  const dispatch = useAppDispatch()

  //Selectors
  const sites = useAppSelector(siteStore.selectors.list)
  const selectedSiteIds = useAppSelector(siteStore.selectors.selected)
  const risks = useAppSelector(physicalRiskStore.selectors.list)
  const selectedPhysicalRiskIds = useAppSelector(physicalRiskStore.selectors.selected)
  const families = useAppSelector(physicalRiskStore.selectors.listFamilies)
  const isPopulationActive = useAppSelector(populationStore.selectors.isActive)
  const activePopulation = useAppSelector(populationStore.selectors.active)

  //Si une population est active, restreindre aux risques de cette population
  const baseRisks = isPopulationActive ? risks.filter((r) => r.populationIds.includes(activePopulation.id)) : risks

  //Items to display families
  const selectedRisks = baseRisks.filter((x) => x.populationIds.length > 0)
  const familyIds = flatten(selectedRisks.map((x) => x.RiskFamilyId ?? RISK_FAMILY_UNDEFINED_ID))
  const selectedFamilies = families.filter((x) => familyIds.includes(x.id))
  const familyRiskIdsById = new Map(
    selectedFamilies.map((family) => [
      family.id,
      selectedRisks
        .filter((risk) =>
          family.id === RISK_FAMILY_UNDEFINED_ID ? !risk.RiskFamilyId : risk.RiskFamilyId === family.id
        )
        .map((risk) => risk.id)
    ])
  )
  const selectedFamilyCount = selectedFamilies.filter((family) => {
    const riskIds = familyRiskIdsById.get(family.id) ?? []
    return riskIds.length > 0 && riskIds.every((id) => selectedPhysicalRiskIds.includes(id))
  }).length
  const selectedFilterCount = selectedSiteIds.length + selectedFamilyCount
  const showAdd = selectedFilterCount > 0
  const showRemove = selectedFilterCount > 1

  const filteredSites = sites.filter((site) =>
    isPopulationActive ? site.populationIds.includes(activePopulation.id) : site.populationIds.length > 0
  )
  const selectedSites = filteredSites.filter((site) => selectedSiteIds.includes(site.id))
  const selectedFamiliesSummary = selectedFamilies.filter((family) => {
    const riskIds = familyRiskIdsById.get(family.id) ?? []
    return riskIds.length > 0 && riskIds.every((id) => selectedPhysicalRiskIds.includes(id))
  })

  function replaceWithSite() {
    dispatch(physicalRiskStore.actions.select([]))
  }

  function removeSite(siteId: string) {
    dispatch(siteStore.actions.select(selectedSiteIds.filter((id) => id !== siteId)))
  }

  function replaceWithFamily() {
    dispatch(siteStore.actions.select([]))
  }

  return (
    <FilterSidePanel width={400}>
      <ListItem>
        <PopulationsSearch />
        <RiskSort store={populationStore} keys={{ risk: "cRiskMax.cValueEffective", size: "size", name: "name" }} />
      </ListItem>
      <SiteFilterBlock
        sites={filteredSites}
        selectedSites={selectedSites}
        showAdd={showAdd}
        showRemove={showRemove}
        onReplace={replaceWithSite}
        onRemoveSite={removeSite}
      />
      <RiskFamilyFilterBlock
        families={selectedFamilies}
        selectedFamiliesSummary={selectedFamiliesSummary}
        familyRiskIdsById={familyRiskIdsById}
        showAdd={showAdd}
        showRemove={showRemove}
        onReplace={replaceWithFamily}
      />
    </FilterSidePanel>
  )
}
